import { message } from 'antd';
import download from '../../../../actions/helpers/download';
import { BCP_NUMBERS } from '../../../../global/bcpNumbers';
import {
  getCachedReferralCallTemplates,
  getVisibleSmsTemplatesForCurrentUser,
} from '../../../../util/referralTemplateStorage';
import { buildSmsTemplateMessage } from '../../../../util/smsTemplateVariables';
import { selectionHasReplyRow } from '../../../../util/inboxRowCapabilities';
import {
  handleSendToChart,
  handleCreateChart,
  handleCreateTask,
  buildCreateTaskModalData,
  handleDirectAutoUpload,
  handleSftpUpload,
  normalizeRecord,
} from '../../../../components/chartActions/helpers';
import { getArchiveSoftBlockStatuses } from './helpers/archiveSoftBlock';
import {
  archiveFailed,
  unarchiveFailed,
  ARCHIVE_FAILURE_MESSAGE,
  UNARCHIVE_FAILURE_MESSAGE,
} from '../../../../util/archiveOutcome';
import { openMergeConfirm } from '../../shared/openMergeConfirm';
import {
  hasFeatureInLocalStorage,
  INBOX_BACKGROUND_REFRESH_FEATURE,
  INBOX_ACTION_CLARITY_FEATURE,
} from '../../../../global/featureFlags';
import { deriveAssignmentTargets } from './referralActionState';

// ENG-2825 (plan D3, "replacing `isAReplySelected` one-for-one"): the actions that have no
// stated capability of their own -- chart actions, Mark Completed, SMS, MAIRA Call, Referral
// Replies, Transfer to department -- keep the exact predicate the eight `isAReplySelected`
// copies used, so a ready complete document's TOOLBAR behaviour is unchanged in Phase 1.
// Action parity for a ready return is ENG-3438. What Phase 1 changes is navigation (row click,
// double click, title link, viewer previous/next), which reads `opensMessageDrawerOnClick`.
// The actions that DO have a capability read it directly, via referralActionState.
// Module scope, not rebuilt per render (react-doctor/prefer-module-scope-pure-function).
export const isAReplySelected = (records) => selectionHasReplyRow(records);

/* global toastr */

const useReferralActions = ({
  selectedData,
  data: allDataOnPage,
  openModal,
  printUserRecord,
  downloadUrl,
  downloadFilename,
  downloadZip,
  archiveReceivedRecords,
  unarchiveReceivedRecords,
  triggerAutoUpload,
  autoUploadEmr,
  currentOrganizationId,
  getDefaultSmsTemplate,
  defaultSmsTemplate,
  getDefaultOutboundCallTemplate,
  defaultOutboundCallTemplate,
  onOpenEhrDrawer,
  refreshReferrals,
  canForward = false,
  triggerSFTPUpload,
  fromReferralManagement = true,
  useReferralArchiveWarnings = true,
}) => {
  const oneRecordSelected = selectedData.length === 1;
  const selectedAll = selectedData[0] === 'all';
  const hasOneReceivedRecord = allDataOnPage.length === 1;


  const isFormRecord = (records) => records.some((d) => d?.form_id);

  const isEmailRecord = (records) => records.some((d) => d?.attachment_id);

  const isUploadDisabled = (receiverType, rowDataOrReceiver) => {
    const isBCP = localStorage.m8_org === 'BCP Group';
    const receiver = rowDataOrReceiver && typeof rowDataOrReceiver === 'object'
      ? rowDataOrReceiver.receiver
      : rowDataOrReceiver;
    if (receiverType === 'received_fax') {
      if (isBCP && !BCP_NUMBERS.includes(receiver)) return true;
      return false;
    }
    if (receiverType === 'email_attachment' && !isBCP) return false;
    if (receiverType === 'jotform_submission' && !isBCP) return false;
    if (receiverType === 'reply') {
      if (isBCP) return true;
      return !rowDataOrReceiver?.has_resolvable_record;
    }
    if (receiverType === 'receiver' && !isBCP) return false;
    return true;
  };

  const normalizeFileNamePart = (value) => String(value || 'unknown').replace(/ /g, '_');

  const buildSelectedRecord = (record) => {
    if (!record) return null;
    return normalizeRecord(record, {
      parentType: record.parent_type,
      dynamicSendToken: record.send_token,
      uploadReceiverType: record.receiver_type,
      isEmailAttachment: record.receiver_type === 'email_attachment',
      isEmailSubmission: record.is_email_submission === true,
      isSentContext: false,
    });
  };

  const downloadRecord = async () => {
    try {
      if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
        const file = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
        const { file_token: recordId, record_status: recordStatus } = file;
        if (recordStatus === 'revoked') return toastr.error('You can not download a record that has been revoked');
        const downloadData = await printUserRecord(recordId);
        const resolvedDownloadUrl = downloadData?.signedUrl || downloadUrl;
        const resolvedDownloadFilename = downloadData?.downloadFilename || downloadFilename || 'file';
        if (!resolvedDownloadUrl) {
          toastr.error('Unable to download this record. Try refreshing and downloading again.');
          return;
        }
        await download(resolvedDownloadUrl, resolvedDownloadFilename);
        return;
      }
      openModal({ type: 'DOWNLOAD_ZIP' });
      const recordsToDownload = (selectedAll ? allDataOnPage : selectedData)
        .filter((record) => record.record_status !== 'revoked')
        .map((x) => x.file_token);
      await downloadZip(recordsToDownload);
    } catch {
      message.error({ content: 'Unable to download the selected record(s). Please try again.' });
    }
  };

  const editTag = () => {
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const { send_token: sendToken, tag, receiver_type: receiverType } = receiver;
      openModal({
        type: 'ADD_LABEL_TO_RECORD',
        data: {
          reportAiDetails: {
            isLabelEdited: selectedData[0].is_label_edited,
            hasWrongLabelingBeenReported: selectedData[0].has_wrong_labeling_been_reported,
            aiStatus: selectedData[0].ai_status,
          },
          record: sendToken,
          tag,
          receiverType,
        },
      });
      return;
    }
    if (selectedData.length > 1) {
      const sendTokens = selectedData.map((d) => d.send_token);
      const receiverTypes = selectedData.map((d) => d.receiver_type);
      openModal({
        type: 'ADD_LABEL_TO_MULTIPLE_RECORDS',
        data: { record: sendTokens, receiverTypes },
      });
      return;
    }
    const sendTokens = (selectedAll ? allDataOnPage : selectedData).map((x) => x.send_token);
    openModal({ type: 'ADD_LABEL_TO_RECORD', data: { record: sendTokens } });
  };

  const newChart = () => {
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const {
        patient_dob: PatientDOB, patient_name: PatientName, send_token: sendToken,
        receiver_type: receiverType, api_credentials: apiCredentials,
      } = receiver;
      openModal({
        type: 'CREATE_NEW_CHART',
        data: {
          name: PatientName,
          dob: PatientDOB,
          record: sendToken,
          gender: selectedData[0].patient_gender,
          phoneNumber: selectedData[0].patient_phone_number,
          address: selectedData[0].patient_address,
          zipCode: selectedData[0].patient_zip_code,
          city: selectedData[0].patient_city,
          practitionerName: selectedData[0].practitioner_name,
          icdCodes: selectedData[0].icd_codes,
          primaryInsurancePoBox: selectedData[0].primary_insurance_po_box,
          primaryInsuranceState: selectedData[0].primary_insurance_state,
          secondaryInsurancePoBox: selectedData[0].secondary_insurance_po_box,
          secondaryInsuranceState: selectedData[0].secondary_insurance_state,
          marketingRep: selectedData[0].marketing_rep,
          receiverType,
          receivedAt: selectedData[0].received_at,
          callerName: selectedData[0].caller_name,
          documentCategory: selectedData[0].document_category,
          referral: '',
          patient_phone_number: selectedData[0].patient_phone_number,
          patient_address: selectedData[0].patient_address,
          patient_zip_code: selectedData[0].patient_zip_code,
          patient_city: selectedData[0].patient_city,
          state: selectedData[0].patient_state,
          patient_email: selectedData[0].patient_email,
          patient_first_name: selectedData[0].patient_first_name,
          patient_last_name: selectedData[0].patient_last_name,
          patient_middle_name: selectedData[0].patient_middle_name,
          referring_provider: selectedData[0].referring_provider,
          provider_office: selectedData[0].provider_office,
          primary_plan: selectedData[0].primary_plan,
          groupNumber: selectedData[0].group_number,
          primaryMemberId: selectedData[0].primary_member_id,
          payerName: selectedData[0].payer_name,
          primaryPlanName: selectedData[0].primary_plan_name,
          secondaryPlanName: selectedData[0].secondary_plan_name,
          secondaryPayerName: selectedData[0].secondary_payer_name,
          secondaryMemberId: selectedData[0].secondary_member_id,
          secondaryGroupNumber: selectedData[0].secondary_group_number,
          apiCredentials,
          authorizationNumber: selectedData[0].authorization_number,
          authorizationFromDate: selectedData[0].authorization_from_date,
          authorizationToDate: selectedData[0].authorization_to_date,
          authorizationNumberOfVisits: selectedData[0].authorization_number_of_visits,
          authorizationDescription: selectedData[0].authorization_description,
        },
      });
      return;
    }
    const sendTokens = (selectedAll ? allDataOnPage : selectedData).map((x) => x.send_token);
    openModal({ type: 'ADD_LABEL_TO_RECORD', data: { record: sendTokens } });
  };

  const createTask = () => {
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const {
        send_token: sendToken,
        receiver_type: receiverType,
      } = receiver;
      const record = selectedAll ? receiver : (selectedData[0] || receiver);
      openModal({
        type: 'CREATE_TASK',
        data: buildCreateTaskModalData(record, {
          receiver,
          sendToken,
          receiverType,
        }),
      });
    }
  };

  const notifyModal = () => {
    // ENG-3339: Notify only sheds its department chips where a "Transfer to
    // department" entry point stands beside it. Both surfaces that call this hook
    // (ReferralActionBar, RowQuickActions) render that entry point under the same
    // flag, so the same read decides both — Notify never has to guess.
    const emailOnly = hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE);
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const { send_token: sendToken, tag } = receiver;
      openModal({
        type: 'NOTIFY',
        data: {
          record: sendToken,
          tag,
          receiverType: selectedData[0].receiver_type,
          emailOnly,
        },
      });
      return;
    }
    const dataToUse = selectedAll ? allDataOnPage : selectedData;
    const sendTokens = dataToUse.map((x) => x.send_token);
    const receiverTypes = dataToUse.map((x) => x.receiver_type);
    openModal({
      type: 'NOTIFY',
      data: { record: sendTokens, receiverType: receiverTypes, emailOnly },
    });
  };

  // ENG-3339: same selection semantics as notifyModal, opening the department-only
  // modal instead. Only rendered by callers when `inbox_action_clarity` is on.
  const transferToDepartmentModal = () => {
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      // `hasOneReceivedRecord` is page-scoped, so this branch is also reached with
      // an empty or stale selection when the page holds a single record. Take the
      // receiver type from the SAME `receiver` the send token came from, the way
      // createTask and editPatientOpen do — reading `selectedData[0]` here throws
      // on an empty selection and mispairs the token with a stale record's type.
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const { send_token: sendToken, tag, receiver_type: receiverType } = receiver;
      openModal({
        type: 'TRANSFER_TO_DEPARTMENT',
        data: {
          record: sendToken,
          tag,
          receiverType,
        },
      });
      return;
    }
    const dataToUse = selectedAll ? allDataOnPage : selectedData;
    const sendTokens = dataToUse.map((x) => x.send_token);
    const receiverTypes = dataToUse.map((x) => x.receiver_type);
    openModal({
      type: 'TRANSFER_TO_DEPARTMENT',
      data: { record: sendTokens, receiverType: receiverTypes },
    });
  };

  const editPatientOpen = () => {
    const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
    const { send_token: sendToken, tag, receiver_type: receiverType } = receiver;
    openModal({
      type: 'EDIT_PATIENT',
      data: {
        patient: selectedData[0],
        fromPage: 'received',
        record: sendToken,
        tag,
        receiverType,
      },
    });
  };

  function handleDropdownSendToChart(selectedRecord) {
    const record = buildSelectedRecord(selectedRecord);
    handleSendToChart({
      record,
      dependencies: { onOpenEhrDrawer },
    });
  }

  function handleDropdownCreateChart(selectedRecord) {
    const record = buildSelectedRecord(selectedRecord);
    handleCreateChart({
      record,
      dependencies: { onOpenEhrDrawer },
    });
  }

  function handleDropdownCreateTask(selectedRecord) {
    const record = buildSelectedRecord(selectedRecord);
    handleCreateTask({
      record,
      dependencies: { onOpenEhrDrawer },
    });
  }

  const performAction = (recordId, action) => {
    // Use the feature flag value loaded at mount; avoid per-click flag fetches.
    const type = canForward === true ? action : 'RAISE_FLAG';

    if (type === 'FORWARD_RECORD') {
      const selectedRecord = (selectedAll ? allDataOnPage : selectedData)
        .find((record) => record?.send_token === recordId)
        || selectedData.find((record) => record?.send_token === recordId)
        || allDataOnPage.find((record) => record?.send_token === recordId)
        || (oneRecordSelected && !selectedAll ? selectedData[0] : null)
        || (hasOneReceivedRecord ? allDataOnPage[0] : null);

      if (selectedRecord) {
        handleDropdownSendToChart(selectedRecord);
        return;
      }

      openModal({
        type: 'RAISE_FLAG',
        data: recordId,
        inboxLink: '/app/received',
        featureFlag: 'fwd_to_emr',
      });
      return;
    }

    openModal({
      type,
      data: recordId,
      inboxLink: '/app/received',
      featureFlag: 'fwd_to_emr',
    });
  };

  const refreshReferralList = async () => {
    if (typeof refreshReferrals === 'function') {
      await refreshReferrals(window.location.search || '');
    }
  };

  // The soft-block warning stays. Unlike a plain "are you sure", it names the
  // referral statuses that should not normally be archived — information the user
  // cannot get anywhere else on this page — and it performs the archive itself once
  // confirmed. Everything else archives immediately: the ARCHIVE_RECORD confirmation
  // modal is now only on the record viewers (full-page and slideout).
  //
  // Failure is read from the resolved result rather than from the shared
  // `state.records.archiveErrorMessage` slice — see util/archiveOutcome. Both
  // referral surfaces are received-side (/app/referred, /app/received), so this is
  // always the receivers endpoint.
  const archiveRecordOpen = async (tokens, softBlockStatuses = []) => {
    if (softBlockStatuses.length > 0) {
      openModal({
        type: 'REFERRAL_ARCHIVE_WARNING',
        data: {
          tokens,
          softBlockStatuses,
        },
      });
      return;
    }

    const result = await archiveReceivedRecords(tokens);
    if (archiveFailed(result)) {
      message.error({ content: ARCHIVE_FAILURE_MESSAGE });
      return;
    }
    message.success({ content: 'Successfully archived selected record(s)' });
    // With inbox_background_refresh on, no explicit list refresh: the archive action
    // requests a silent background refresh (REQUEST_INBOX_TABLE_REFRESH), which
    // avoids the full-table loading spinner that refreshReferralList would show.
    // Off, keep the legacy foreground refetch. Mirrors unarchiveRecordOpen below.
    if (!hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)) {
      await refreshReferralList();
    }
  };

  // `unarchiveFailed` rather than a bare `result?.error`: the endpoint scopes by
  // organization/department and returns 200 having unarchived nothing when no token
  // matched, which the error check alone reported as success.
  const unarchiveRecordOpen = async (tokens) => {
    const result = await unarchiveReceivedRecords(tokens);
    if (unarchiveFailed(result)) {
      message.error({ content: UNARCHIVE_FAILURE_MESSAGE });
    } else {
      message.success({ content: 'Successfully unarchived selected record(s)' });
    }
    // With inbox_background_refresh on, no explicit list refresh: the unarchive
    // action requests a silent background refresh (REQUEST_INBOX_TABLE_REFRESH),
    // which avoids the full-table loading spinner that refreshReferralList would
    // show. Off, keep the legacy foreground refetch. We still refetch after a
    // failure here because this hook drives a soft refresh, so the toast survives
    // while we resync any partial/unscoped unarchive result back into the list.
    if (!hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)) {
      await refreshReferralList();
    }
  };

  // Both merge entry points now route through the shared MERGE_RECORDS_CONFIRM
  // modal, which collects the retained-value overrides and performs the merge.
  // The `onMerged` continuation runs the surface-specific post-merge step.
  const handleMergeRecords = (objects) => openMergeConfirm({
    objects,
    openModal,
    buildOnMerged: () => async () => {
      await refreshReferralList();
    },
  });

  const handleMergeAndArchiveRecords = (objects) => openMergeConfirm({
    objects,
    openModal,
    archiveAfter: true,
    buildOnMerged: (tokens) => {
      const softBlockStatuses = useReferralArchiveWarnings ? getArchiveSoftBlockStatuses(objects) : [];
      return async () => {
        await archiveRecordOpen(tokens, softBlockStatuses);
      };
    },
  });

  const autoUploadRecord = async (selectedRecord) => {
    const {
      patient_name: patientName,
      patient_dob: patientDOB,
      send_token: sendToken,
      receiver_type: receiverType,
      api_credentials: apiCredentials,
    } = selectedRecord;
    if (autoUploadEmr !== 'EMA' && (!patientName || !patientDOB)) {
      message.error({
        content: 'Cannot upload a document without a valid name and date of birth. Edit record details and try again.',
      });
    } else {
      const fileNameSupported = ['Kareo', 'OncoEMR', 'Nymbl', 'EMA', 'AllegianceMD', 'Elation'];

      if (fileNameSupported.includes(autoUploadEmr) || (autoUploadEmr === 'ECW' && currentOrganizationId > 4206) || (autoUploadEmr === 'CareTracker' && currentOrganizationId === 4193)) {
        let recordFileName = selectedRecord.document_title;
        if (!recordFileName && (selectedRecord.receiver_type === 'jotform_submission' || selectedRecord.is_email_submission === true)) {
          recordFileName = `${normalizeFileNamePart(selectedRecord.created_at)}_${normalizeFileNamePart(selectedRecord.sender_name)}`;
        } else if (!recordFileName && selectedRecord.receiver_type === 'reply') {
          // Merged reply document (ENG-3012): a stable human title, never the
          // raw date+sender synthesis. document_title (parent fallback) wins above.
          recordFileName = selectedRecord.sender_name ? `Reply - ${selectedRecord.sender_name}` : 'Reply';
        } else if (!recordFileName) {
          recordFileName = `${normalizeFileNamePart(selectedRecord.received_at)}_${normalizeFileNamePart(selectedRecord.caller_name)}`;
        }
        openModal({
          type: 'AUTO_UPLOAD',
          data: {
            fileName: recordFileName,
            providerName: selectedData[0].practitioner_name,
            dateOfService: selectedData[0].date_of_service,
            patientName: selectedData[0].patient_name,
            patientFirstName: selectedData[0].patient_first_name,
            patientMiddleName: selectedData[0].patient_middle_name,
            patientLastName: selectedData[0].patient_last_name,
            patientDOB: selectedData[0].patient_dob,
            documentCategory: selectedData[0].document_category,
            sendToken,
            receiverType,
            autoUploadEmr,
            apiCredentials,
            authorizationNumber: selectedData[0].authorization_number,
            authorizationFromDate: selectedData[0].authorization_from_date,
            authorizationToDate: selectedData[0].authorization_to_date,
            authorizationNumberOfVisits: selectedData[0].authorization_number_of_visits,
            authorizationDescription: selectedData[0].authorization_description,
            fromReferralManagement,
            currentOrganizationId,
          },
        });
      } else if (autoUploadEmr === 'AdvancedMD') {
        openModal({
          type: 'AUTO_UPLOAD',
          data: {
            providerName: selectedData[0].practitioner_name,
            patientName: selectedData[0].patient_name,
            patientFirstName: selectedData[0].patient_first_name,
            patientMiddleName: selectedData[0].patient_middle_name,
            patientLastName: selectedData[0].patient_last_name,
            patientDOB: selectedData[0].patient_dob,
            documentCategory: selectedData[0].document_category,
            sendToken,
            receiverType,
            autoUploadEmr,
          },
        });
      } else if (autoUploadEmr === 'Brightree') {
        openModal({
          type: 'AUTO_UPLOAD',
          data: {
            patientName: selectedData[0].patient_name,
            patientFirstName: selectedData[0].patient_first_name,
            patientMiddleName: selectedData[0].patient_middle_name,
            patientLastName: selectedData[0].patient_last_name,
            patientDOB: selectedData[0].patient_dob,
            icdCodes: selectedData[0].icd_codes,
            primaryInsurancePoBox: selectedData[0].primary_insurance_po_box,
            primaryInsuranceState: selectedData[0].primary_insurance_state,
            secondaryInsurancePoBox: selectedData[0].secondary_insurance_po_box,
            secondaryInsuranceState: selectedData[0].secondary_insurance_state,
            marketingRep: selectedData[0].marketing_rep,
            sendToken,
            receiverType,
          },
        });
      } else {
        handleDirectAutoUpload({
          triggerAutoUpload,
          sendToken,
          receiverType,
        });
      }
    }
  };

  const smsMessage = async (selectedRecord, smsMessageFeature) => {
    const {
      send_token: sendToken,
      receiver_type: receiverType,
      patient_name: patientName,
      patient_dob: patientDOB,
      patient_phone_number: patientPhoneNumber,
      referring_provider: referringProvider,
    } = selectedRecord;
    if (!patientName || !patientDOB) {
      message.error({
        content: 'Cannot send a message without a valid patient name and date of birth. Edit record details and try again.',
      });
    } else {
      try {
        const fetchedTemplate = await getDefaultSmsTemplate(sendToken, receiverType);
        const resolvedTemplate = fetchedTemplate || defaultSmsTemplate;
        if (!resolvedTemplate) {
          message.error('Failed to load default message template');
          return;
        }
        const userTemplates = getVisibleSmsTemplatesForCurrentUser();
        openModal({
          type: 'SMS_MESSAGE',
          data: {
            sendToken,
            receiverType,
            patientName,
            referringProvider,
            messageTemplates: userTemplates,
            messageText: buildSmsTemplateMessage({
              templateMessage: resolvedTemplate.message,
              patientName,
              referringProvider,
            }),
            url: resolvedTemplate.short_url,
            originalUrl: resolvedTemplate.url,
            patientPhoneNumber,
            smsMessageFeature,
          },
        });
      } catch (err) {
        console.error('Error fetching default template:', err);
        message.error('Failed to load default message template');
      }
    }
  };

  const mairaCall = async (selectedRecord, mairaCallFeature) => {
    const {
      send_token: sendToken,
      receiver_type: receiverType,
      patient_name: patientName,
      patient_dob: patientDOB,
      patient_phone_number: patientPhoneNumber,
      referring_provider: referringProvider,
    } = selectedRecord;

    if (!patientName || !patientDOB) {
      message.error({
        content: 'Cannot queue a MAIRA call without a valid patient name and date of birth. Edit record details and try again.',
      });
      return;
    }

    try {
      const fetchedTemplate = await getDefaultOutboundCallTemplate(sendToken, receiverType);
      const resolvedDefault = (fetchedTemplate && fetchedTemplate.id ? fetchedTemplate : null)
        || (defaultOutboundCallTemplate && defaultOutboundCallTemplate.id ? defaultOutboundCallTemplate : null);

      // localStorage.referral_call_templates is already scoped to referral templates this
      // user can access.
      // No need to re-filter on the client; doing so drops department templates whose
      // membership isn't reflected in localStorage.departments.
      const allCallTemplates = getCachedReferralCallTemplates();
      const dedupedTemplates = [...allCallTemplates];
      if (resolvedDefault && !dedupedTemplates.some((template) => template.id === resolvedDefault.id)) {
        dedupedTemplates.unshift(resolvedDefault);
      }

      if (dedupedTemplates.length === 0) {
        message.error('No MAIRA call templates available. Ask an admin to create one.');
        return;
      }

      const selectedTemplateId = resolvedDefault?.id || dedupedTemplates[0].id;

      openModal({
        type: 'MAIRA_CALL',
        data: {
          sendToken,
          receiverType,
          patientName,
          patientPhoneNumber,
          referringProvider,
          callTemplates: dedupedTemplates,
          selectedTemplateId,
          mairaCallFeature,
        },
      });
    } catch (err) {
      console.error('Error fetching default MAIRA call template:', err);
      message.error('Failed to load default MAIRA call template');
    }
  };

  const referralReply = async (selectedRecord) => {
    const {
      send_token: sendToken,
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      patient_dob: patientDOB,
      file_token: fileToken,
    } = selectedRecord;
    await openModal({
      type: 'REFERRAL_REPLY',
      data: {
        sendToken,
        caller_name: selectedRecord.sender_email,
        ownerType: selectedRecord.owner_type,
        ownerId: selectedRecord.owner_id,
        patientDOB,
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        referringProvider: selectedRecord.referring_provider,
        fileToken,
        appointmentDate: selectedRecord.referral_scheduled_appointment?.appointment_time || null,
      },
    });
  };

  const uploadToSFTPFolder = (sendToken) => handleSftpUpload({
    triggerSFTPUpload,
    sendToken,
  });

  const createAssignedDocument = () => {
    // One record keeps the original modal verbatim: it is also reached from the
    // record viewer, the row quick actions and the product tutorial.
    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const { send_token: sendToken, receiver_type: receiverType } = receiver;
      openModal({
        type: 'ASSIGN_RECORD',
        data: { record: sendToken, receiverType },
      });
      return;
    }

    // Same derivation the toolbar gates on. An unassignable row refuses the whole
    // selection rather than quietly dropping out of the batch.
    const { rows, records, assignable } = deriveAssignmentTargets({
      selectedData,
      allDataOnPage,
      selectedAll,
    });

    if (!assignable) return;

    openModal({ type: 'ASSIGN_MULTIPLE_RECORDS', data: { records, rows } });
  };

  return {
    downloadRecord,
    editTag,
    newChart,
    createTask,
    notifyModal,
    transferToDepartmentModal,
    editPatientOpen,
    performAction,
    archiveRecordOpen,
    unarchiveRecordOpen,
    autoUploadRecord,
    smsMessage,
    mairaCall,
    referralReply,
    createAssignedDocument,
    uploadToSFTPFolder,
    handleMergeRecords,
    handleMergeAndArchiveRecords,
    handleDropdownSendToChart,
    handleDropdownCreateChart,
    handleDropdownCreateTask,
    isAReplySelected,
    isFormRecord,
    isEmailRecord,
    isUploadDisabled,
    buildSelectedRecord,
  };
};

export default useReferralActions;
